import {addShadowsForAllModels, compressMeshForMobileDevices} from "../../../../../../helpers/sqft3dArSceneHelper";
import SqftStore from "../../../../../canvas/store/sqftStore";
import TaskRunner from "../helpers/TaskRunner";

class BaseModelLoader {
  constructor({ scene, loader, modelScale, typeKey }) {
    this.scene      = scene;
    this.loader     = loader;
    this.modelScale = modelScale;
    this.typeKey    = typeKey;
    this.rawCache = new Map();      // GLTF cache by URL
    this.materialCache = {};        // material cache by URL
    this.materialPromises = {};     // promises for loading materials
    this.instances = new Map();     // active instances by key

    // Debug logging flag
    this.debugLogging = false;

    // Global texture counter for debugging
    if (!window.textureDebug) {
      window.textureDebug = {
        textures: new Map(),
        materials: new Map()
      };
    }
  }

  _makeKey(data) {
    return `${this.typeKey}__${data.partId}__${data.id || data.shapeId}`;
  }

  _addUserData(instance, data, key) {
    instance.userData = { ...data, _type: this.typeKey, _key: key };
    return instance
  }

  // Preload all unique materials
  async preloadMaterials(dataArray) {
    // Collect unique material URLs
    const uniqueMaterialUrls = new Set();

    dataArray.forEach(data => {
      if (data.material_url) {
        uniqueMaterialUrls.add(data.material_url.trim());
      }
    });

    if (this.debugLogging) {
      console.log(`Preloading ${uniqueMaterialUrls.size} unique materials...`);
    }

    // Create tasks for loading materials
    const materialTasks = Array.from(uniqueMaterialUrls).map(url => {
      return () => this._loadMaterial(url);
    });

    // Use TaskRunner to load materials
    const isMobile = store.getState().serverState.isMobile || /iPad|iPhone/.test(navigator.userAgent);

    if (isMobile) {
      await TaskRunner.sequential(materialTasks);
    } else {
      await TaskRunner.parallel(materialTasks);
    }

    if (this.debugLogging) {
      console.log('All materials preloaded!');
    }
  }

  async load(data) {
    const key = this._makeKey(data);
    if (this.instances.has(key)) {
      return this.instances.get(key);
    }

    const url = await this.getUrl(data);
    let raw = this.rawCache.get(url);
    if (!raw) {
      raw = await this._loadGltf(url);
      //this.rawCache.set(url, raw); //QCDES-7036 //todo find out why cache not working properly
    }

    const instance = await this.clone(raw, data);
    this.scene.add(instance);
    this.instances.set(key, instance);

    return instance;
  }

  async getUrl(data) {
    return data.url;
  }

  getModelPosition(data) {
    return {
      x: data.x * this.modelScale,
      y: data.y * this.modelScale,
      z: data.z * this.modelScale
    }
  }

  _loadGltf(url) {
    return new Promise((res, rej) => {
      this.loader.load(url, gltf => {
        if (this.debugLogging) {
          console.log(`Loaded GLTF from ${url}`);

          // Debug: check what's inside loaded model
          let textureCount = 0;
          gltf.scene.traverse(child => {
            if (child.isMesh && child.material) {
              const mat = child.material;
              console.log(`  - Mesh: ${child.name}, Material: ${mat.name || 'unnamed'}`);

              ['map', 'normalMap', 'roughnessMap', 'metalnessMap'].forEach(mapName => {
                if (mat[mapName]) {
                  textureCount++;
                  console.log(`    - ${mapName}: ${mat[mapName].uuid}`);
                }
              });
            }
          });
          console.log(`  Total textures in model: ${textureCount}`);
        }

        res(gltf.scene);
      }, undefined, err => {
        console.error(`GLTF load failed for ${url}`, err);
        rej(err);
      });
    });
  }

  async clone(rawScene, data) {
    const inst = rawScene.clone(true);
    const key = this._makeKey(data);
    const position = this.getModelPosition(data);
    inst.position.set(position.x, position.y, position.z);

    if (data.rotation) {
      inst.rotation.y = window.THREE.MathUtils.degToRad(data.rotation);
    }

    // Apply material if specified
    if (data.material_url) {
      if (this.debugLogging) {
        console.log(`Cabinet ${data.shapeId} requests material: ${data.material_url}`);
      }
      const sharedMat = await this._loadMaterial(data.material_url);

      if (sharedMat) {
        inst.traverse(child => {
          if (child.isMesh) {
            compressMeshForMobileDevices(child);

            // IMPORTANT: Do NOT clone material!
            child.material = sharedMat;

            // Debug
            if (this.debugLogging) {
              console.log(`Applied material ${sharedMat.uuid} to ${child.name} in ${data.shapeId}`);
            }
          }
        });
      }
    }

    await this.postProcess(inst, data);

    // Final debug
    if (this.debugLogging) {
      this._debugTextureUsage(inst, data);
    }

    //this._removeExtraTextures(inst);

    return this._addUserData(inst, data, key);
  }

  _debugTextureUsage(inst, data) {
    console.log(`Cabinet ${data.shapeId} texture usage:`);
    inst.traverse(child => {
      if (child.isMesh && child.material) {
        const mat = child.material;
        const textures = [];

        ['map', 'normalMap', 'roughnessMap', 'metalnessMap'].forEach(mapName => {
          if (mat[mapName]) {
            textures.push(`${mapName}:${mat[mapName].uuid.substr(0,8)}`);

            // Add to global counter
            window.textureDebug.textures.set(mat[mapName].uuid, {
              type: mapName,
              material: mat.uuid,
              cabinet: data.shapeId
            });
          }
        });

        if (textures.length > 0) {
          console.log(`  - ${child.name}: ${textures.join(', ')}`);
        }
      }
    });

    // Show total statistics
    console.log(`Total unique textures so far: ${window.textureDebug.textures.size}`);
  }

  async _loadMaterial(url) {
    // Normalize URL for proper caching
    const normalizedUrl = url.trim();

    // Check cache for ready materials
    if (this.materialCache[normalizedUrl]) {
      if (this.debugLogging) {
        console.log(`Using cached material for ${normalizedUrl}`);
      }
      return this.materialCache[normalizedUrl];
    }

    // Check if this material is already loading
    if (this.materialPromises[normalizedUrl]) {
      if (this.debugLogging) {
        console.log(`Waiting for already loading material: ${normalizedUrl}`);
      }
      return this.materialPromises[normalizedUrl];
    }

    if (this.debugLogging) {
      console.log(`Loading new material from ${normalizedUrl}`);
    }

    // Create promise and save it
    this.materialPromises[normalizedUrl] = new Promise((res, rej) => {
      this.loader.load(normalizedUrl, async gltf => {
        const mesh = gltf.scene.getObjectByProperty('type','Mesh');
        if (!mesh) {
          if (this.debugLogging) {
            console.log(`No mesh found in loaded GLTF for material ${normalizedUrl}`);
          }
          // Remove promise after completion
          delete this.materialPromises[normalizedUrl];
          return res(undefined);
        }

        const originalMat = mesh.material;

        // Create ONE shared material for this URL
        const sharedMat = new window.THREE.MeshPhysicalMaterial({
          color: (originalMat.color && originalMat.color.clone()) || new window.THREE.Color(1,1,1),
          metalness: originalMat.metalness || 0,
          roughness: originalMat.roughness || 0.5,
        });

        // Copy only needed textures
        if (originalMat.map) {
          sharedMat.map = originalMat.map;
          if (this.debugLogging) {
            console.log(`  - Added diffuse map: ${originalMat.map.uuid}`);
          }
        }

        if (originalMat.metalnessMap) {
          sharedMat.metalnessMap = originalMat.metalnessMap;
          if (this.debugLogging) {
            console.log(`  - Added metalness map: ${originalMat.metalnessMap.uuid}`);
          }
        }

        if (originalMat.normalMap) {
          sharedMat.normalMap = originalMat.normalMap;
          if (this.debugLogging) {
            console.log(`  - Added normalMap map: ${originalMat.normalMap.uuid}`);
          }
        }

        if (originalMat.roughnessMap) {
          sharedMat.roughnessMap = originalMat.roughnessMap;
          if (this.debugLogging) {
            console.log(`  - Added roughnessMap map: ${originalMat.roughnessMap.uuid}`);
          }
        }

        sharedMat.needsUpdate = true;

        // Save to cache with normalized URL
        this.materialCache[normalizedUrl] = sharedMat;

        // Remove promise after completion
        delete this.materialPromises[normalizedUrl];

        if (this.debugLogging) {
          console.log(`Created shared material ${sharedMat.uuid} with ${Object.keys(sharedMat).filter(k => k.includes('Map') && sharedMat[k]).length} maps`);
        }

        res(sharedMat);
      }, undefined, err => {
        console.error(`Error loading material ${normalizedUrl}`, err);
        // Remove promise on error
        delete this.materialPromises[normalizedUrl];
        rej(err);
      });
    });

    return this.materialPromises[normalizedUrl];
  }

  _removeExtraTextures(inst) {
    inst.traverse(child => {
      if (child.isMesh && child.material) {
        const mat = child.material;

        // Remove maps that are not critical for visuals
        mat.normalMap = null;
        mat.roughnessMap = null;
        mat.aoMap = null;
        mat.displacementMap = null;
        mat.emissiveMap = null;
        mat.lightMap = null;
        mat.bumpMap = null;

        // If metalness and roughness are the same - remove roughness
        if (mat.metalnessMap === mat.roughnessMap) {
          mat.roughnessMap = null;
        }

        mat.needsUpdate = true;
      }
    });
  }

  async postProcess(instance, data) {
    void instance
    void data
  }

  remove(data) {
    const key = this._makeKey(data);
    const inst = this.instances.get(key);
    if (!inst) return;

    this.scene.remove(inst);

    inst.traverse(child => {
      if (child.isMesh) {
        child.geometry.dispose();
        // Do NOT remove shared materials - they are used by other objects
      }
    });

    this.instances.delete(key);
  }

  async reload(data) {
    this.remove(data);
    const instance = await this.load(data);
    addShadowsForAllModels(SqftStore.viewGL.scene);
    return instance;
  }
}

export default BaseModelLoader
